import type { CSSProperties, ReactNode } from 'react'

/**
 * The redesign's tables are CSS grids, not <table>s: a dark uppercase header
 * band on --tile, zebra rows, and a horizontal scroll container so wide column
 * sets stay usable without the page itself scrolling sideways.
 */

export type Align = 'left' | 'right' | 'center'

export interface GridColumn {
  key: string
  label: ReactNode
  align?: Align
}

export function GridTableShell({
  children,
  style,
}: {
  children: ReactNode
  style?: CSSProperties
}) {
  return (
    <div
      style={{
        border: '1px solid var(--line)',
        borderRadius: 12,
        overflow: 'hidden',
        background: 'var(--paper)',
        ...style,
      }}
    >
      {children}
    </div>
  )
}

export function GridHead({
  columns,
  template,
  minWidth,
}: {
  columns: GridColumn[]
  template: string
  minWidth?: number | string
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: template,
        gap: 10,
        padding: '11px 14px',
        background: 'var(--tile)',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: 'var(--onTileMuted)',
        minWidth,
      }}
    >
      {columns.map((c) => (
        <div
          key={c.key}
          style={{
            minWidth: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: c.align ?? 'left',
          }}
        >
          {c.label}
        </div>
      ))}
    </div>
  )
}

export function GridRow({
  children,
  template,
  minWidth,
  zebra,
  onClick,
  style,
}: {
  children: ReactNode
  template: string
  minWidth?: number | string
  zebra?: boolean
  onClick?: () => void
  style?: CSSProperties
}) {
  return (
    <div
      className={onClick ? 'fx-row-hoverable' : undefined}
      onClick={onClick}
      style={{
        display: 'grid',
        gridTemplateColumns: template,
        gap: 10,
        padding: '12px 14px',
        borderBottom: '1px solid var(--line2)',
        alignItems: 'center',
        fontSize: 13,
        cursor: onClick ? 'pointer' : undefined,
        background: zebra ? 'var(--zebra)' : 'transparent',
        minWidth,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

/** One truncating cell. Numbers get the right alignment, ids the mono face. */
export function GridCell({
  children,
  align = 'left',
  mono,
  strong,
  color,
  size = 13,
  style,
}: {
  children: ReactNode
  align?: Align
  mono?: boolean
  strong?: boolean
  color?: string
  size?: number
  style?: CSSProperties
}) {
  return (
    <div
      style={{
        minWidth: 0,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        textAlign: align,
        fontFamily: mono ? 'var(--mono)' : undefined,
        fontWeight: strong ? 700 : 400,
        color: color ?? 'var(--text)',
        fontSize: mono ? Math.min(size, 12) : size,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

export function GridFooter({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px' }}>
      {children}
    </div>
  )
}

/** Thin usage bar, as used for PO consumption and supplier share. */
export function MiniBar({
  fraction,
  color = 'var(--accent)',
  height = 8,
}: {
  fraction: number
  color?: string
  height?: number
}) {
  const pct = Math.max(0, Math.min(1, Number.isFinite(fraction) ? fraction : 0)) * 100
  return (
    <div
      style={{
        height,
        borderRadius: 4,
        background: 'var(--line2)',
        overflow: 'hidden',
      }}
    >
      <div style={{ height: '100%', borderRadius: 4, width: `${pct}%`, background: color }} />
    </div>
  )
}
